import { useCallback, useContext } from 'react';
import { hideAppSpinner, showAppMessage, showAppSpinner } from '@rainbow-modules/app';
import useHttpMutation from 'data/firestore/useHttpMutation';
import context from 'components/OutboundCallProvider/context';

interface Props {
    agentId: string;
    customerId: string;
}

interface Req {
    phoneNumber: string;
    personaId?: string;
    voiceMailPersonaId?: string;
    transferTo?: string;
    workflowId?: string;
}

interface Res {
    code: string;
    message: string;
    callSid: string;
}

interface Params {
    customerPhoneNumber: string;
    responderId?: string;
    phoneNumber?: string;
    voiceMailResponderId?: string;
    workflowId?: string;
}

const useCallCustomer = ({ agentId, customerId }: Props) => {
    const { mutateAsync: callWithAIPersona } = useHttpMutation<Req, Res>({
        method: 'POST',
        pathname: `/agents/${agentId}/customers/${customerId}/call`,
    });

    const { mutateAsync: callUsingPhone } = useHttpMutation<Req, Res>({
        method: 'POST',
        pathname: `/agents/${agentId}/customers/${customerId}/call-bridge`,
    });

    const { addOutboundCall } = useContext(context);

    const callCustomer = useCallback(async ({
        responderId,
        phoneNumber,
        customerPhoneNumber,
        voiceMailResponderId,
        workflowId,
    }: Params) => {
        try {
            showAppSpinner();
            let callFn: typeof callWithAIPersona | typeof callUsingPhone;
            let body: Req;
            if (responderId) {
                callFn = callWithAIPersona;
                body = {
                    phoneNumber: customerPhoneNumber,
                    personaId: responderId,
                    voiceMailPersonaId: voiceMailResponderId,
                };
            } else if (workflowId) {
                callFn = callWithAIPersona;
                body = { phoneNumber: customerPhoneNumber, workflowId };
            } else {
                callFn = callUsingPhone;
                body = { phoneNumber: customerPhoneNumber, transferTo: phoneNumber };
            }
            const { callSid } = await callFn({ body });
            addOutboundCall(callSid);
        } catch (error) {
            showAppMessage({
                variant: 'error',
                message: (error as Error).message,
                timeout: 5000,
            });
        }
        hideAppSpinner();
    }, [addOutboundCall, callUsingPhone, callWithAIPersona]);

    return {
        callCustomer,
    };
};

export default useCallCustomer;
